import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

interface Props {
  open: boolean;
  universityName: string;
  onClose: () => void;
  onConfirm: (password: string) => void;
}

export const AdmissionDeclineModal = ({ open, universityName, onClose, onConfirm }: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleNext = () => setStep((s) => s + 1);

  const handleConfirm = () => {
    if (!password) {
      setError(t('decline.errorEmpty'));
      return;
    }
    onConfirm(password);
  };

  const reset = () => {
    setStep(1);
    setPassword('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-6 w-6" />
            {t('decline.title', { step })}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {step === 1 && (
            <div className="space-y-4">
              <p className="font-bold text-lg">{t('decline.confirmTitle', { name: universityName })}</p>
              <p className="text-gray-600">{t('decline.warning')}</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-md text-red-800">
                <h4 className="font-bold mb-2">{t('decline.alertTitle')}</h4>
                <p className="text-sm">{t('decline.alertMessage')}</p>
              </div>
              <p className="font-bold">{t('decline.finalConfirm')}</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-gray-900 font-bold">{t('decline.passwordPrompt')}</p>
              <div className="space-y-2">
                <Label>{t('decline.passwordLabel')}</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('decline.passwordPlaceholder')}
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t('decline.cancel')}
          </Button>

          {step < 3 ? (
            <Button variant="destructive" onClick={handleNext}>
              {t('decline.next')}
            </Button>
          ) : (
            <Button variant="destructive" onClick={handleConfirm} disabled={!password}>
              {t('decline.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
